import { Component, Input } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { OrderService } from '@/app/shared/services/order.service';
import { Order, OrderItem, OrderStatus, ReOrderResponseModel } from '@/app/shared/interface/order.interface';
import { CartService } from '@/app/shared/services/cart.service';
import { Router } from '@angular/router';
import { BaseResponseDataModel } from '@/app/shared/interface/base-response.interface';

@Component({
  selector: 'app-order-card',
  templateUrl: './order-card.component.html',
  styleUrls: ['./order-card.component.scss']
})
export class OrderCardComponent {
  @Input() order: Order;
  @Input() isChildOrder: boolean = false;
  @Input() orderNumber: number;

  public isGettingInvoice: { [key: string]: boolean } = {};
  public isReordering = false;

  constructor(
    private orderService: OrderService,
    private toastService: ToastrService,
    private cartService: CartService,
    private router: Router,
    private _sanitizer: DomSanitizer
  ) { }

  ngOnInit() {
    if (this.order) {
      this.order.displayStatus = this.getOrderStatus(this.order.orderStatus);
    }
  }

  get totalQuantity(): number {
    return (this.order?.items ?? []).reduce((sum, item) => sum + item.quantity, 0);
  }

  get canReOrder(): boolean {
    return this.order?.orderStatus === OrderStatus.Done || this.order?.orderStatus === OrderStatus.Cancelled;
  }

  reOrder() {
    if (this.isReordering) {
      return;
    }
    this.isReordering = true;
    this.orderService.reOrder(this.order.orderId).subscribe({
      next: (res: ReOrderResponseModel) => {
        this.isReordering = false;
        if (!res) {
          return;
        }
        if (res.isSuccess) {
          this.toastService.success("Đã thêm các sản phẩm vào giỏ hàng");
          if (res.data.length) {
            this.toastService.info(
              "Một số SP/đơn hàng không còn khả dụng (tạm hết hàng hoặc dừng bán), vui lòng kiểm tra lại giỏ hàng trước khi đặt hàng!",
              "",
              { timeOut: 10000 }
            );
          }
          this.cartService.refreshCart();
          this.router.navigateByUrl('/cart');
        } else if (res.data.length) {
          this.toastService.error("Tất cả SP/đơn hàng này không còn khả dụng, vui lòng kiểm tra lại hoặc liên hệ với nhân viên để được tư vấn!");
        } else {
          this.toastService.error("Đặt hàng lại lỗi, vui lòng thử lại sau");
        }
      },
      error: (err: any) => {
        this.isReordering = false;
        this.toastService.error(err?.error?.message);
        this.cartService.refreshCart();
      }
    });
  }

  getOrderStatus(status: OrderStatus) {
    switch (status) {
      case OrderStatus.Created:
        return { value: "Đang xác nhận", icon: "created" };
      case OrderStatus.Progressing:
        return { value: "Đang xử lý", icon: "progressing" };
      case OrderStatus.Shipping:
        return { value: "Đang vận chuyển", icon: "shipping" };
      case OrderStatus.Done:
        return { value: "Đã Hoàn Thành", icon: "done" };
      case OrderStatus.Cancelled:
        return { value: "Đã hủy", icon: "cancelled" };
      case OrderStatus.Paid:
        return { value: "Đã thanh toán", icon: "paid" };
      case OrderStatus.PartialPaid:
        return { value: "Thanh toán một phần", icon: "partial-paid" };
      case OrderStatus.Slipted:
        return { value: "Đã tách đơn", icon: "slipted" };
      default:
        return { value: "Không xác định", icon: "progressing" };
    }
  }

  getTotalItems(items: OrderItem[]) {
    const itemsCount = items?.length ?? 0;
    const totalQuantity = (items ?? []).reduce((current, next) => current + next.quantity, 0);
    return this._sanitizer.bypassSecurityTrustHtml(`Sản phẩm: <span style="color: #000000;">${itemsCount}</span> - Tổng SL <span style="color: #000000;">${totalQuantity}</span>`);
  }

  gotoDetail(order: Order) {
    this.orderService.setlectedOrder = order;
  }

  getOrderInvoice(order: Order, invCode: string) {
    this.isGettingInvoice[invCode] = true;
    const newTab = window.open('', '_blank');

    if (!newTab) {
      this.isGettingInvoice[invCode] = false;
      alert('Mở trang mới bị chặn. Vui lòng cho phép mở tab mới để xem Hóa đơn.');
      return;
    }

    this.orderService.getOrderInvoiceUrl(order.orderId, invCode).subscribe({
      next: (res: BaseResponseDataModel<string>) => {
        if (res.isSuccess) {
          newTab.location.href = res.data;
        } else {
          console.error(res);
          newTab.close();
          this.toastService.error("Có lỗi khi lấy hóa đơn, vui lòng thử lại sau");
        }
      },
      error: (err: any) => {
        newTab.close();
        this.toastService.error("Có lỗi khi lấy hóa đơn, vui lòng thử lại sau.");
        console.error(err);
        this.isGettingInvoice[invCode] = false;
      },
      complete: () => {
        this.isGettingInvoice[invCode] = false;
      }
    });
  }
}
